<template>
    <b-table-simple hover small bordered responsive class="batch-ingredient-record mb-0">
        <b-thead>
            <b-tr>
                <b-th>Ingredient</b-th>
                <template v-if="showActivityColumns">
                    <b-th class="text-right">Scaled</b-th>
                    <b-th class="text-right">Activity Factor</b-th>
                    <b-th class="text-right">Adjusted</b-th>
                </template>
                <b-th class="text-right">Weigh-Out</b-th>
                <b-th>Lot Number</b-th>
                <b-th>Expiration Date</b-th>
            </b-tr>
        </b-thead>
        <b-tbody>
            <b-tr v-for="ingredient in ingredients" :key="ingredient.id">
                <b-td>
                    <span>{{ ingredient.drugName || 'Unknown' }}</span>
                    <b-badge v-if="isOverridden(ingredient)" variant="warning" class="ml-1"
                        :title="ingredient.overrideReason || 'Weigh-out overridden by hand'">
                        <i class="fas fa-user-edit" /> Overridden
                    </b-badge>
                    <b-badge v-if="ingredient.missingCoaFlag" variant="warning" class="ml-1"
                        title="Flagged for activity factor but filled from a lot with no usable COA — factor 1.0 applied">
                        <i class="fas fa-exclamation-triangle" /> No COA
                    </b-badge>
                    <div v-if="coaText(ingredient)" class="small"
                        :class="coaNotApplied(ingredient) ? 'text-muted font-italic' : 'text-secondary'">
                        COA: {{ coaText(ingredient) }}
                        <span v-if="coaNotApplied(ingredient)">
                            (not applied — fixed factor {{ ingredient.fixedConversionFactor }})
                        </span>
                    </div>
                    <div v-else-if="ingredient.fixedConversionFactor != null" class="small text-muted font-italic">
                        Fixed conversion factor {{ ingredient.fixedConversionFactor }} applied
                    </div>
                </b-td>
                <template v-if="showActivityColumns">
                    <b-td class="text-right text-nowrap">
                        <span v-if="ingredient.scaledQuantity != null">{{ formatQty(ingredient.scaledQuantity) }} {{ ingredient.unitOfMeasure }}</span>
                        <span v-else class="text-muted" title="Not recorded for this batch">—</span>
                    </b-td>
                    <b-td class="text-right text-nowrap">
                        <span v-if="ingredient.activityFactor != null">{{ formatFactor(ingredient.activityFactor) }}</span>
                        <span v-else class="text-muted" title="Activity factor was not applied to this ingredient">—</span>
                    </b-td>
                    <b-td class="text-right text-nowrap">
                        <span v-if="ingredient.adjustedQuantity != null">{{ formatQty(ingredient.adjustedQuantity) }} {{ ingredient.unitOfMeasure }}</span>
                        <span v-else class="text-muted" title="Not recorded for this batch">—</span>
                    </b-td>
                </template>
                <b-td class="text-right text-nowrap">
                    <strong :class="{ 'text-danger': isOverridden(ingredient) }">
                        {{ formatQty(ingredient.quantity) }} {{ ingredient.unitOfMeasure }}
                    </strong>
                    <div v-if="isOverridden(ingredient)" class="small text-muted text-wrap override-note">
                        computed <s>{{ formatQty(computedQuantity(ingredient)) }} {{ ingredient.unitOfMeasure }}</s>
                        <br />
                        <span v-if="ingredient.overrideReason">{{ ingredient.overrideReason }} — </span>{{ ingredient.overrideBy }}<span v-if="ingredient.overrideDate">, {{ formatDateTime(ingredient.overrideDate) }}</span>
                    </div>
                </b-td>
                <b-td>
                    <span v-if="ingredient.lotNumber">{{ ingredient.lotNumber }}</span>
                    <span v-else class="text-muted">Not recorded</span>
                    <div v-if="ingredient.inventoryId" class="small text-muted" title="The inventory record the ingredient was deducted from">
                        inventory #{{ ingredient.inventoryId }}
                    </div>
                </b-td>
                <b-td>
                    <span v-if="ingredient.expirationDate">{{ formatDate(ingredient.expirationDate) }}</span>
                    <span v-else class="text-muted">Not recorded</span>
                </b-td>
            </b-tr>
        </b-tbody>
    </b-table-simple>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { CompoundBatchIngredients } from '@/models/Drug/CompoundDrug';
import { formatActivityFactor } from '@/util/activityFactor';
import {
    coaNotApplied,
    describeIngredientCoa,
    formatRecordQuantity,
    hasActivitySnapshot,
    isOverridden,
} from '@/util/compoundBatchReview';

/**
 * The ingredient rows of a compound batch record as the pharmacist and the auditor read them: what
 * was weighed out, how the number was reached (scaled quantity, activity factor, adjusted quantity,
 * the COA that drove it or the fixed factor that replaced it), any manual override with its reason
 * and author, the missing-COA marker, and the lot consumed. Everything shown is the snapshot saved
 * on the batch — a later edit to the lot's COA does not change it.
 *
 * The activity columns appear only when some row carries the snapshot, so batches made before the
 * feature or with it switched off keep the plain four-column table.
 */
@Component({
    name: 'CompoundBatchIngredientRecord',
})
export default class CompoundBatchIngredientRecord extends Vue {
    @Prop({ type: Array, default: () => [] }) ingredients!: CompoundBatchIngredients[];

    /** Precision for quantities and factors on rows with an activity snapshot (the pharmacy's ActivityFactorDecimalPlaces). */
    @Prop({ type: Number, default: 4 }) decimalPlaces!: number;

    get showActivityColumns(): boolean {
        return this.ingredients.some(ingredient => hasActivitySnapshot(ingredient));
    }

    /** Rows without a snapshot were saved at the batch-scaled quantity and read best at two places, as before. */
    get quantityDecimals(): number {
        return this.showActivityColumns ? this.decimalPlaces : 2;
    }

    isOverridden(ingredient: CompoundBatchIngredients): boolean {
        return isOverridden(ingredient);
    }

    coaText(ingredient: CompoundBatchIngredients): string {
        return describeIngredientCoa(ingredient);
    }

    coaNotApplied(ingredient: CompoundBatchIngredients): boolean {
        return coaNotApplied(ingredient);
    }

    /** The quantity the override replaced: the adjusted quantity, or the scaled one where no adjustment applied. */
    computedQuantity(ingredient: CompoundBatchIngredients): number | null {
        return ingredient.adjustedQuantity != null ? ingredient.adjustedQuantity : ingredient.scaledQuantity;
    }

    formatQty(value: number | null | undefined): string {
        return formatRecordQuantity(value, this.quantityDecimals);
    }

    formatFactor(factor: number | null | undefined): string {
        return formatActivityFactor(factor, this.decimalPlaces);
    }

    formatDate(value: string | Date | null): string {
        if (!value) return 'N/A';
        return new Date(value).toLocaleDateString();
    }

    formatDateTime(value: string | Date | null): string {
        if (!value) return 'N/A';
        return new Date(value).toLocaleString();
    }
}
</script>

<style scoped>
.batch-ingredient-record .override-note {
    max-width: 16rem;
    margin-left: auto;
}
</style>
